import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';

import Header from '../layouts/client/dashboard/header/Header';
import Navbar from '../layouts/client/dashboard/navbar/Navbar';
import { useQuery } from 'react-fetching-library';
import { getClientZSDataAction } from '../services/request';
import toast from 'react-hot-toast';

const RootStyle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  overflow: 'hidden',
  margin: '0 5px'
});

const HeaderStyle = styled('div')(({ theme }) => ({
  padding: '20px 0px',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const ClientZSContext = React.createContext(null);

export default () => {
  const { loading, payload, error } = useQuery(getClientZSDataAction());

  const [purchaserData, setPurchaserData] = React.useState({});
  const [userData, setUserData] = React.useState({});
  const [orders, setOrders] = React.useState([]);
  const [selectedOrderId, setSelectedOrderId] = React.useState('');

  React.useEffect(() => {
    if (!error && !loading) {
      setPurchaserData(payload.purchaser_data);
      setUserData(payload.user_data);
      setOrders(payload.orders || []);

      if (payload.orders?.length) {
        setSelectedOrderId(String(payload.orders[0].id));
      }
    }

    if (error) {
      toast.error('Wystąpił błąd');
    }
  }, [loading, error, payload]);

  const orderData = React.useMemo(
    () => orders.find(order => String(order.id) === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  const advanceAmount = Number(orderData?.order_value || 0) * 0.3;

  const advanceLeftToPay = Math.max(
    0,
    advanceAmount - Number(orderData?.sum_of_advances || 0)
  );
  const paid =
    Number(orderData?.order_value) -
    Number(orderData?.left_to_pay);

  return (
    <ClientZSContext.Provider
      value={{
        userData,
        purchaserData,
        orderData,
        orders,
        selectedOrderId,
        setSelectedOrderId,
        advanceAmount,
        advanceLeftToPay,
        paid
      }}
    >
      {loading ? (
        <Box height={300}>
          <CircularProgress />
        </Box>
      ) : (
        <RootStyle>
          <HeaderStyle>
            <Header />
          </HeaderStyle>

          <MainStyle>
            <Navbar />

            {orders.length > 1 && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel>Zamówienie</InputLabel>
                  <Select
                    value={selectedOrderId}
                    label="Zamówienie"
                    onChange={e => setSelectedOrderId(e.target.value)}
                  >
                    {orders.map(order => (
                      <MenuItem key={order.id} value={String(order.id)}>
                        {order.identifier}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box height={5} />
            <Outlet />
          </MainStyle>
        </RootStyle>
      )}
    </ClientZSContext.Provider>
  );
};

export const useClientZS = () => React.useContext(ClientZSContext);
